import React from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { Home, MapPin, Mail, User, Pencil, Trash2 } from 'lucide-react';
import { House, User as UserType } from '../../services/types';
import { useUserData } from '../../hooks';

interface HouseCardProps {
    house: House;
    ownerName: string;
    onEdit: (house: House) => void;
    onDelete: (publicId: string) => void;
}

const HouseCard: React.FC<HouseCardProps> = ({
    house,
    ownerName,
    onEdit,
    onDelete,
}) => {
    const { canEdit, canDelete } = useUserData();

    return (
        <Card
            className="h-100 border-0 shadow-sm d-flex flex-column"
            style={{
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: canEdit ? 'pointer' : 'default',
            }}
            onMouseEnter={(e) => {
                if (canEdit) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
            }}
            onClick={() => canEdit && onEdit(house)}
        >
            <Card.Body className="d-flex flex-column">
                {/* Header with House Icon */}
                <div className="d-flex align-items-start mb-3">
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            backgroundColor: house.color || '#e0f2fe',
                            marginRight: '12px',
                            flexShrink: 0,
                        }}
                    >
                        <Home size={24} style={{ color: house.color ? '#fff' : '#0284c7' }} />
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <h6
                            className="mb-1"
                            style={{
                                fontWeight: 600,
                                color: '#111827',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {house.name || 'House'}
                        </h6>
                        {house.website && (
                            <a
                                href={house.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    fontSize: '0.75rem',
                                    color: '#3b82f6',
                                    textDecoration: 'none',
                                }}
                            >
                                Visit Website
                            </a>
                        )}
                    </div>
                </div>

                {/* House Details */}
                <div className="mb-3 flex-grow-0">
                    <div className="d-flex align-items-start mb-2">
                        <MapPin size={16} className="me-2 mt-1" style={{ color: '#6b7280', flexShrink: 0 }} />
                        <div style={{ fontSize: '0.875rem', color: '#374151', lineHeight: '1.4' }}>
                            <div>{house.address}</div>
                            <div className="text-muted">{house.location}</div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                        <Mail size={16} className="me-2" style={{ color: '#6b7280' }} />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                            {house.post_code}
                        </span>
                    </div>
                    <div className="d-flex align-items-center">
                        <User size={16} className="me-2" style={{ color: '#6b7280' }} />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                            Owner: <strong>{ownerName}</strong>
                        </span>
                    </div>
                </div>

                {/* Value Per Service */}
                {house.value_per_service && (
                    <div className="mb-2 flex-grow-0">
                        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>
                            Value per Service: <strong>€{house.value_per_service.toFixed(2)}</strong>
                        </span>
                    </div>
                )}

                {/* Observation */}
                {house.observation && (
                    <div className="mb-3 flex-grow-0">
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', lineHeight: '1.4' }}>
                            {house.observation.length > 80
                                ? `${house.observation.substring(0, 80)}...`
                                : house.observation}
                        </span>
                    </div>
                )}

                {/* Color Badge */}
                {house.color && (
                    <div className="mb-3 pb-2 flex-grow-0" style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <Badge
                            style={{
                                backgroundColor: house.color,
                                color: '#fff',
                                fontSize: '0.75rem',
                            }}
                        >
                            {house.color}
                        </Badge>
                    </div>
                )}

                {/* Spacer to push buttons to bottom */}
                <div className="flex-grow-1"></div>

                {/* Action Buttons */}
                <div className="d-flex gap-2 mt-2 flex-shrink-0">
                    {canEdit && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="flex-fill"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(house);
                            }}
                        >
                            <Pencil size={14} className="me-1" />
                            Edit
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            className="flex-fill"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(house.public_id);
                            }}
                        >
                            <Trash2 size={14} className="me-1" />
                            Delete
                        </Button>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default HouseCard;